import { useEffect, Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";

import Text from "../../../components/ui/Text";
import Touchable from "../../../components/ui/Touchable";
import useEmailLogin from "../../../services/webLogin/useEmailLogin";
import useResendSmsCode from "../../../services/webLogin/useResendSmsCode";
import cn from "../../../utils/cn";
import { MarketType } from "../../../types";

import { CountDownTime, CountDownTimeJP } from "../constants";
import { AccountType } from "../types";

export default function CountDownAndResend({
  accountType,
  showSmsDrawer,
  account,
  timeLeft,
  setTimeLeft,
  onResendSuccess,
  market,
}: {
  accountType: AccountType;
  showSmsDrawer: boolean;
  account: string;
  timeLeft: number;
  setTimeLeft: Dispatch<SetStateAction<number>>;
  onResendSuccess: () => void;
  market: MarketType;
}) {
  const { t } = useTranslation();

  useEffect(() => {
    if (timeLeft === 0 || !showSmsDrawer) return;

    const intervalId = setInterval(() => {
      setTimeLeft((prevTime) => prevTime - 1);
    }, 1000);

    return () => clearInterval(intervalId);
  }, [timeLeft, showSmsDrawer, setTimeLeft]);

  const mutationForResendSms = useResendSmsCode();
  const mutationForResendEmail = useEmailLogin();
  const mutationForResend =
    accountType === "mail" ? mutationForResendEmail : mutationForResendSms;

  const handleResend = () => {
    if (mutationForResend.isLoading) return;

    mutationForResend.mutate(account, {
      onSuccess: () => {
        setTimeLeft(market === "JP" ? CountDownTimeJP : CountDownTime);
        onResendSuccess();
      },
    });
  };

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  const minutesLabel = `${minutes}分`;
  const secondsLabel = `${String(seconds).padStart(2, "0")}${t("seconds")}`;
  const countDownLabel =
    minutes > 0
      ? `${minutesLabel}${seconds > 0 ? secondsLabel : ""}`
      : `${seconds} ${t("seconds")}`;

  return (
    <div className="flex h-11 w-fit flex-1 items-center justify-center">
      {timeLeft > 0 ? (
        <Text className="text-grey4">{countDownLabel}</Text>
      ) : (
        <Touchable
          className={cn(
            "flex h-9 w-fit items-center rounded-full border border-solid border-black8 px-4",
            mutationForResend.isLoading
              ? "pointer-events-none opacity-[0.38]"
              : "",
          )}
          onClick={handleResend}
        >
          <Text className="text-nowrap">{t("resend")}</Text>
        </Touchable>
      )}
    </div>
  );
}
